import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Video, Hash, ArrowLeft, Sparkles, Loader, Clock, MapPin, Heart } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { BABASAHEB_IMG } from "../components/BabasahebPortrait";
import { toast } from "sonner";

const FALLBACK_DOMAIN = "meet.jit.si";

/**
 * Public guest entry page for video meetings — does NOT require login.
 *
 * URL forms supported:
 *   /join                  — empty form, asks for code + name
 *   /join?code=XXXX-YYYY   — code prefilled (links shared via Email/WhatsApp)
 *
 * Once joined, the guest sees ONLY the Jitsi room and a small footer.
 * No sidebar, no other meeting codes, no platform navigation.
 */
export default function GuestJoin() {
  const [params] = useSearchParams();
  const initialCode = (params.get("code") || "").toUpperCase();
  const [code, setCode] = useState(initialCode);
  const [displayName, setDisplayName] = useState(() => {
    try { return localStorage.getItem("abs:guest:name") || ""; } catch { return ""; }
  });
  const [meeting, setMeeting] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setCode(initialCode); }, [initialCode]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!code.trim()) return toast.error("Please enter the meeting code.");
    if (!displayName.trim()) return toast.error("Please enter your name so the host can welcome you.");
    setBusy(true);
    try {
      const r = await api.post("/platform/meetings/guest-join", { code: code.trim() });
      try { localStorage.setItem("abs:guest:name", displayName.trim()); } catch { /* ignore */ }
      setMeeting(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not join, check the code.");
    } finally {
      setBusy(false);
    }
  };

  if (meeting) return <GuestRoom meeting={meeting} displayName={displayName} />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1F4D] via-[#1a2f6b] to-[#1a1f3d] text-pearl flex flex-col" data-testid="guest-join-page">
      {/* Top brand strip */}
      <header className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full overflow-hidden border border-saffron/40 flex-shrink-0">
            <img src={BABASAHEB_IMG} alt="Babasaheb" style={{ objectPosition: "50% 22%" }} className="w-full h-full object-cover" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-pearl text-base leading-tight truncate">Ambedkarite Buddhist Global Network UK</div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-saffron/85">Educate · Agitate · Organise</div>
          </div>
        </Link>
        <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-xs text-pearl/60 hover:text-pearl">
          <ArrowLeft size={12} /> Back to site
        </Link>
      </header>

      {/* Form */}
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-saffron mb-3">
              <Sparkles size={10} /> Guest Entry
            </div>
            <h1 className="font-display text-3xl sm:text-4xl text-pearl">Join a Sangha meeting</h1>
            <p className="mt-2 text-pearl/65 text-sm leading-relaxed">
              You don't need an account. Just enter the invite code your host shared
              and your name, that's it.
            </p>
          </div>

          <form
            onSubmit={submit}
            className="mt-7 bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4 backdrop-blur-sm"
          >
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest text-saffron mb-1.5">Your name</span>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Anand · Priya"
                maxLength={60}
                data-testid="guest-name"
                className="w-full bg-white/5 border border-white/15 rounded-md px-3 py-2.5 text-pearl placeholder-pearl/30 focus:outline-none focus:border-saffron"
                required
                autoFocus
              />
              <p className="text-[10px] text-pearl/40 mt-1">Shown to the host so they can welcome you.</p>
            </label>

            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest text-saffron mb-1.5">Meeting code</span>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="BHIM-7A3K"
                maxLength={20}
                autoCapitalize="characters"
                data-testid="guest-code"
                className="w-full bg-white/5 border border-white/15 rounded-md px-3 py-2.5 text-pearl placeholder-pearl/30 font-mono tracking-widest text-center uppercase focus:outline-none focus:border-saffron"
                required
              />
              {initialCode && (
                <p className="text-[10px] text-saffron/85 mt-1.5">
                  ✓ Code prefilled from your invite link.
                </p>
              )}
            </label>

            <button
              type="submit"
              disabled={busy || !code.trim() || !displayName.trim()}
              data-testid="guest-join-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-saffron text-navy rounded-md text-sm font-medium hover:bg-saffron/90 disabled:opacity-50 transition-colors"
            >
              {busy ? <><Loader size={14} className="animate-spin" /> Joining…</> : <><Video size={14} /> Join meeting</>}
            </button>

            <p className="text-[11px] text-pearl/45 text-center italic">
              By joining you agree to be respectful of the Sangha. The Three Refuges welcome all.
            </p>
          </form>

          <div className="mt-6 text-center">
            <p className="text-pearl/55 text-xs">
              Already a member?{" "}
              <Link to="/login" className="text-saffron hover:text-saffron-light underline decoration-dotted">
                Sign in for the full platform
              </Link>
            </p>
          </div>
        </div>
      </main>

      <footer className="px-5 py-4 text-center text-[10px] uppercase tracking-[0.25em] text-pearl/35 border-t border-white/10">
        Ambedkarite Buddhist Global Network UK · Pragya · Karuna · Samata · Bandhutva
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GuestRoom — minimal Jitsi shell. NO sidebar, NO other meeting links.
// ─────────────────────────────────────────────────────────────────────────────
function GuestRoom({ meeting, displayName }) {
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const [joined, setJoined] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const loadScript = (domain) => new Promise((resolve, reject) => {
      if (window.JitsiMeetExternalAPI) return resolve();
      const s = document.createElement("script");
      s.src = `https://${domain}/external_api.js`;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.body.appendChild(s);
    });

    const init = async () => {
      try {
        // Ask backend for a JaaS guest token if JaaS is configured;
        // otherwise fall back to plain meet.jit.si
        let domain = FALLBACK_DOMAIN;
        let actualRoom = meeting.room_name;
        let jwt = undefined;
        try {
          const r = await api.post("/platform/jaas/guest-token", {
            code: meeting.code,
            display_name: displayName || "Sangha guest",
          });
          if (r.data?.token) {
            domain = r.data.domain || "8x8.vc";
            actualRoom = r.data.room_name_full || meeting.room_name;
            jwt = r.data.token;
          }
        } catch (e) { /* JaaS not configured, fall through */ }

        await loadScript(domain);
        if (cancelled) return;
        if (apiRef.current) return;
        apiRef.current = new window.JitsiMeetExternalAPI(domain, {
          roomName: actualRoom,
          jwt,
          parentNode: containerRef.current,
          width: "100%",
          height: "100%",
          userInfo: { displayName: displayName || "Sangha Guest" },
          configOverwrite: {
            prejoinPageEnabled: false,
            disableDeepLinking: true,
            enableClosePage: false,
            enableLobbyChat: false,
            requireDisplayName: false,
            startWithAudioMuted: true,
          },
          interfaceConfigOverwrite: {
            TOOLBAR_BUTTONS: [
              "microphone", "camera", "fullscreen", "fodeviceselection", "hangup",
              "chat", "raisehand", "videoquality", "tileview", "select-background",
            ],
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
          },
        });
        apiRef.current.on("videoConferenceJoined", () => setJoined(true));
        apiRef.current.on("readyToClose", () => navigate("/"));
      } catch (e) {
        toast.error("Could not load the video meeting. Please try again.");
      }
    };
    init();

    return () => {
      cancelled = true;
      try { if (apiRef.current) apiRef.current.dispose(); } catch (e) { /* ignore */ }
      apiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting.room_name, displayName]);

  const fmtTime = () => {
    if (!meeting.start_at) return null;
    try {
      return new Date(meeting.start_at).toLocaleString("en-GB", {
        weekday: "short", day: "numeric", month: "short",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return null; }
  };
  const startTime = fmtTime();

  return (
    <div className="h-screen w-screen flex flex-col bg-[#0B1F4D] text-pearl" data-testid="guest-room">
      {/* Compact branded header */}
      <header className="flex items-center justify-between gap-3 px-4 py-2.5 bg-navy border-b border-white/10">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-7 h-7 rounded-full overflow-hidden border border-saffron/40 flex-shrink-0">
            <img src={BABASAHEB_IMG} alt="Babasaheb" style={{ objectPosition: "50% 22%" }} className="w-full h-full object-cover" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-pearl truncate">{meeting.title}</div>
            <div className="text-[10px] uppercase tracking-widest text-pearl/50">
              {joined ? "Connected" : "Connecting…"}
              {meeting.host_name && <> · Hosted by <span className="text-saffron/85">{meeting.host_name}</span></>}
            </div>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-3 text-[11px] text-pearl/55 flex-shrink-0">
          {startTime && (
            <span className="inline-flex items-center gap-1"><Clock size={11} /> {startTime}</span>
          )}
          {meeting.location && (
            <span className="inline-flex items-center gap-1 max-w-[14rem] truncate"><MapPin size={11} /> {meeting.location}</span>
          )}
        </div>
      </header>

      {/* Jitsi takes the rest */}
      <div ref={containerRef} className="flex-1" data-testid="guest-jitsi-container" />

      {/* Footer, quiet but useful */}
      <footer className="flex items-center justify-between gap-3 px-4 py-2 bg-navy border-t border-white/10 text-[10px] text-pearl/55">
        <span className="uppercase tracking-[0.25em]">Ambedkarite Buddhist Global Network UK</span>
        <Link
          to="/membership"
          data-testid="guest-membership-cta"
          className="inline-flex items-center gap-1 text-saffron/85 hover:text-saffron uppercase tracking-[0.2em]"
        >
          <Heart size={10} /> Become a member
        </Link>
      </footer>
    </div>
  );
}
